import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { ViewReferralWrapper } from "./styled";
import {
  BackButton,
  Box,
  HeadingStyled,
  SpanStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import { FormDataContainer } from "../settings/styled";
import Breadcrumbs from "../../../components/admin/Breadcrumbs/Breadcrumbs";
import ViewPage from "../../../components/common/pageView/pageView";
import ReferralsApi from "../../../services/api/referralsApi";
import { useDispatch } from "react-redux";
import { setSidebarReferralCount } from "../../../store/slices/helperSlice";
import FileViewerModal from "../../../components/common/fileViewer/FileViewer";
import {
  handleFileDownload,
  handleFilePreview,
  revokeBlobUrl,
} from "../../../helpers/fileDownloadHelper";
import { getModuleOperations } from "../../../utils";
import { 
  AttachmentIcon,
  Download,
  HalfEyeIcon,
 } from "../../../components/styled/AllImages";

const ViewReferral = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { t, i18n: { language } } = useTranslation();
  const navigate = useNavigate();

  const [details, setDetails] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const getReferralDetail = async () => {
    try {
      setDataLoading(true);
      const { data, code } = await ReferralsApi.getReferralDetail(id);
      if (code === 200) {
        setDetails(data);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setDataLoading(false);
    }
  };

  const getUserPendingReferrals = async () => {
    try {
      const { data, code } = await ReferralsApi.getUserPendingReferrals();
      if (code === 200) {
        dispatch(setSidebarReferralCount(data?.pending_count ?? 0));
      } 
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownload = async (filePath) => {
    await handleFileDownload(filePath, setDownloading, {
      starting_download: t("starting_download"),
      downloading: t("downloading"),
      remaining_time: t("remaining_time"),
      download_complete: t("download_complete"),
      download_failed_try_again: t("download_failed_try_again"),
    }, details?.referral_info?.id);
  };

  const handlePreview = async (filePath) => {
    await handleFilePreview(
      filePath,
      setViewingFile,
      setShowModal,
      setDownloading,
      {
        preview_failed_try_again: t("preview_failed_try_again"),
      },
      details?.referral_info?.id
    );
  };

  const handleCloseModal = () => {
    setShowModal(false);
    revokeBlobUrl(viewingFile?.url);
  };

  /**
   * Extract all file paths from polymorphic content hierarchy
   * Returns array of file paths from all levels
   */
  const extractFilesFromContent = (content, files = []) => {
    if (!content) return files;

    // Check if current node has a file path (for FileArchive or CabinetFile)
    if (content.file_path) {
      files.push({
        path: content.file_path,
        name: content.display_name,
        type: content.type
      });
    }

    // Recursively check children
    if (content.next_level && Array.isArray(content.next_level)) {
      content.next_level.forEach(child => {
        if (typeof child === 'object' && child !== null) {
          extractFilesFromContent(child, files);
        }
      });
    }

    return files;
  };

  const renderPreview = (path) => {
    const isDownload = Number(details?.referral_info?.access_type) === 2;

    if (!path) {
      return <span>{path}</span>;
    }

    const decodedPath = decodeURIComponent(path);
    const fileName = decodedPath.split("/").pop();

    return (
      <Box>
        <Box className="d-flex gap-2 align-items-center">
          <Box>
            <AttachmentIcon width={50} height={70} />
            <SpanStyled>{fileName}</SpanStyled>
          </Box>
          {downloading ? (
            <ThemeLoader />
          ) : (
            <Box className="d-flex align-items-center justify-content-center gap-2">
              <Box className="p-2 rounded" style={{backgroundColor: "var(--bgSecondary)"}}>
                <HalfEyeIcon
                  className="cursor-pointer"
                  onClick={() => handlePreview(path)}
                  title={t("view") || "View"}
                  style={{ cursor: "pointer" }}
                />
              </Box>

              {getModuleOperations(3, 6) && isDownload && (
                <Box className="p-2 rounded" style={{backgroundColor: "var(--bgSecondary)"}}>
                  <Download
                    className="cursor-pointer"
                    onClick={() => handleDownload(path)}
                    title={t("download") || "Download"}
                    style={{ cursor: "pointer" }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  /**
   * Render content hierarchy as simple flat sections
   * Cabinet → CabinetFile → FileArchive details
   */
  const renderContentHierarchy = (content, level = 0) => {
    if (!content) return null;

    const renderCabinet = () => {
      if (content.type !== 'Cabinet') return null;
      
      return (
        <Box key="cabinet-section" className="mb-4">
          <HeadingStyled 
            size="18px" 
            weight="500" 
            color="var(--text)" 
            className="mb-3"
          >
            {t("cabinet_info")}
          </HeadingStyled>
          <FormDataContainer className="dataContainer">
            <ViewPage label={t("name")} text={content.display_name} />
            <ViewPage label={t("hash_id")} text={content.hash_id} />
            <ViewPage label={t("description")} text={content.description || "---"} />
          </FormDataContainer>
        </Box>
      );
    };

    const renderCabinetFile = () => {
      if (content.type !== 'CabinetFile') return null;
      
      return (
        <Box key="cabinet-file-section" className="mb-4">
          <HeadingStyled 
            size="18px" 
            weight="500" 
            color="var(--text)" 
            className="mb-3"
          >
            {t("file")}
          </HeadingStyled>
          <FormDataContainer className="dataContainer">
            <ViewPage label={t("name")} text={content.display_name} />
            <ViewPage label={t("hash_id")} text={content.hash_id} />
            <ViewPage label={t("description")} text={content.description || "---"} />
          </FormDataContainer>
        </Box>
      );
    };

    const renderFileArchive = () => {
      if (content.type !== 'FileArchive') return null;

      const ViewArchiveLink = `/cabinets/${details?.content?.hash_id}/files/${details?.content?.next_level[0]?.hash_id}/archives/archive-details/${content.hash_id}`;
      
      return (
        <Box key="file-archive-section" className="mb-4">
          <HeadingStyled 
            size="18px" 
            weight="500" 
            color="var(--text)" 
            className="mb-3"
          >
            {t("archive")}
          </HeadingStyled>
          <FormDataContainer className="dataContainer">
            <ViewPage label={t("name")} text={content.display_name} />
            <ViewPage label={t("hash_id")} text={content.hash_id} />
            <ViewPage label={t("description")} text={content.description || "---"} />
            <ViewPage
              label={t("access_archive")}
              text=""
              render={() => (
                <Box>
                  <button
                    onClick={() => navigate(ViewArchiveLink)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'var(--themeColor)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'opacity 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    {t("access_archive")}
                  </button>
                </Box>
              )}
            />
          </FormDataContainer>
        </Box>
      );
    };

    const renderChildren = () => {
      if (!content.next_level || content.next_level.length === 0) {
        return null;
      }

      return content.next_level.map((child, index) => (
        <Box key={`child-${index}`}>
          {renderContentHierarchy(child, level + 1)}
        </Box>
      ));
    };

    return (
      <>
        {renderCabinet()}
        {renderCabinetFile()}
        {renderFileArchive()}
        {renderChildren()}
      </>
    );
  };

  useEffect(() => {
    getReferralDetail();
    getUserPendingReferrals();
  }, [id, language]);

  return (
    <ViewReferralWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("referrals")}
            onClick={() => navigate("/referrals")}
          />
          <Breadcrumbs />
        </Box>
      </Box>

      <Box className="content-container">
        {dataLoading ? (
          <ThemeLoader />
        ) : (
          details && (
            <Row className="mt-3">
              {/* Referral Context */}
              <Col md={12} className="mb-4">
                <FormDataContainer className="dataContainer">
                  <ViewPage 
                    label={t("date")} 
                    text={details.referral_info?.referral_date} 
                  />
                  <ViewPage
                    label={t("status")}
                    text={details.referral_info?.status === 1 ? t("seen") : t("pending")}
                  />
                  <ViewPage 
                    label={t("note")} 
                    text={details.referral_info?.note || t("no_notes")} 
                  />
                </FormDataContainer>
              </Col>

              {/* Content Hierarchy - Polymorphic Rendering */}
              <Col md={12} className="mb-4">
                {details.content ? (
                  renderContentHierarchy(details.content)
                ) : (
                  <Box style={{ color: 'var(--textMuted)', fontStyle: 'italic' }}>
                    {t("no_content")}
                  </Box>
                )}
              </Col>

              {/* Files/Attachments Section - Extracted from hierarchy */}
              {(() => {
                const allFiles = extractFilesFromContent(details.content);
                return allFiles.length > 0 && (
                  <Col md={12} className="mb-4">
                    <HeadingStyled 
                      size="20px" 
                      weight="400" 
                      color="var(--text)" 
                      className="mb-3"
                    >
                      {t("attachments")}
                    </HeadingStyled>
                    <FormDataContainer className="dataContainer">
                      {allFiles.map((file, index) => (
                        <Box key={`file-${index}`} className="mb-3">
                          <ViewPage
                            label={`${file.name} (${file.type})`}
                            text=""
                            render={() => renderPreview(file.path)}
                          />
                        </Box>
                      ))}
                    </FormDataContainer>
                  </Col>
                );
              })()}

              {/* Parties Involved */}
              <Col md={6} className="mb-4">
                <HeadingStyled 
                  size="20px" 
                  weight="400" 
                  color="var(--text)" 
                  className="mb-2"
                >
                  {t("from")}
                </HeadingStyled>
                <FormDataContainer className="dataContainer">
                  <ViewPage label={t("name")} text={details.sender?.name} />
                  <ViewPage label={t("department")} text={details.sender?.department} />
                  <ViewPage label={t("location")} text={details.sender?.location} />
                </FormDataContainer>
              </Col>

              <Col md={6} className="mb-4">
                <HeadingStyled 
                  size="20px" 
                  weight="400" 
                  color="var(--text)" 
                  className="mb-2"
                >
                  {t("to")}
                </HeadingStyled>
                <FormDataContainer className="dataContainer">
                  <ViewPage label={t("name")} text={details.receiver?.name} />
                  <ViewPage label={t("department")} text={details.receiver?.department} />
                </FormDataContainer>
              </Col>
            </Row>
          )
        )}
      </Box>

      <FileViewerModal
        show={showModal}
        onHide={handleCloseModal}
        fileUrl={viewingFile?.url}
        fileType={viewingFile?.type}
      />
    </ViewReferralWrapper>
  );
};

export default ViewReferral;